import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import type { SearchAssets2Query, SearchAssets2QueryVariables } from '../../../../apis/graphql';
import { Assets2SubclassTypes, getDataFromNodes } from '../../../../apis/graphql';
import type { AssetType } from '../../../../configs';
import type { Asset2, Asset2GQL } from '../../../../models';
import { transformAsset2GQLToAsset2 } from '../../../../utils/assets';
import { getAdCountryListForReview } from '../../../../utils/reviewDerivation';
import type { AdFormValues } from '../../../AdForm/adFormik';
import { SEARCH_ASSETS_2 } from './queries/searchAssets2';

export interface UseSearchAssetsResult {
  loading: boolean;
  error?: ApolloError;
  results: Asset2[]; // return empty array if no results are returned by API
}

export const paramsToQueryVariablesAssets2 = (
  query: string,
  assetTypes: Assets2SubclassTypes,
  countryCode?: string,
  limit?: number
): SearchAssets2QueryVariables => {
  return {
    name: query,
    // Asset 2 manager accepts type as VIDEO, DISPLAY or TEXT
    type: assetTypes.replace('Asset', '').toUpperCase(),
    countryCode,
    limit,
  };
};

export const useSearchAssets = (
  query: string,
  assetTypes?: Assets2SubclassTypes[],
  limit?: number,
  lineItemCountryCode?: string,
  publisherList?: string[]
): UseSearchAssetsResult => {
  const { values } = useFormikContext<AdFormValues>();
  const countryList = useMemo(
    () => getAdCountryListForReview(values.lineItem?.countries, values.targetingTermValues ?? []),
    [values.lineItem?.countries, values.targetingTermValues]
  );
  const trimmedQuery = query.trim();

  const assetTypeToQuery: AssetType = assetTypes ? assetTypes[0] : Assets2SubclassTypes.VideoAsset;

  const variables = paramsToQueryVariablesAssets2(trimmedQuery, assetTypeToQuery, lineItemCountryCode, limit);

  const { data, loading, error } = useQuery<SearchAssets2Query, SearchAssets2QueryVariables>(SEARCH_ASSETS_2, {
    skip: !trimmedQuery,
    variables,
  });

  return {
    loading,
    error,
    results: getDataFromNodes(data, 'searchAssets2', (asset: Asset2GQL) =>
      transformAsset2GQLToAsset2(asset, publisherList, countryList)
    )(),
  };
};
